import yaml from "js-yaml";
import { nanoid } from "nanoid";

import type {
  CpuArchType,
  EndpointType,
  ExposeType,
  PlacementAttributeType,
  PlacementType,
  ProfileGpuModelType,
  SdlBuilderFormValuesType,
  ServiceExposeHTTPProxyType,
  ServiceType
} from "@src/types";
import { CPU_ARCHITECTURES, ReclamationMinWindowSchema, RESERVED_ENV_KEYS } from "@src/types/sdlBuilder/sdlBuilder";
import { CustomValidationError } from "../deploymentData";
import { capitalizeFirstLetter } from "../stringUtils";
import { defaultHttpOptions, SDL_DEFAULT_NEXT_TIMEOUT } from "./data";
import { secretNameOf } from "./sdlSecrets";

export const parseSvcCommand = (command?: string | (string | number | boolean)[]): string =>
  svcTokensOf(command)
    .filter(token => token.length > 0)
    .join("\n");

/** YAML parses unquoted scalars like `0` or `false` into native types, so tokens are stringified instead of filtered as falsy. */
function svcTokensOf(command?: string | (string | number | boolean)[]): string[] {
  if (!command) {
    return [];
  }

  return (typeof command === "string" ? [command] : command).filter(token => token !== null && token !== undefined).map(String);
}

/** The form holds one trimmed, non-empty token per line, so tokens an SDL wrote empty, padded or across lines are kept as written. */
function tokensUnlessOnePerLine(tokens: string[]): string[] | undefined {
  return tokens.some(token => !token || token.includes("\n") || token !== token.trim()) ? tokens : undefined;
}

/**
 * Imports an SDL YAML into builder form values. By default placements are
 * deduplicated by name (a placement's identity is its name), so services that
 * share a placement name reference one record. Pass `placementPerService` for
 * the legacy builder, which edits a placement per service and needs each to
 * stay independent across edits/round-trips.
 */
export const importSimpleSdl = (yamlStr: string, { placementPerService = false }: { placementPerService?: boolean } = {}): SdlBuilderFormValuesType => {
  try {
    const yamlJson = yaml.load(yamlStr) as any;
    const placements: PlacementType[] = [];
    const placementIdByName = new Map<string, string>();
    const services: ServiceType[] = [];

    if (!yamlJson || typeof yamlJson !== "object" || Array.isArray(yamlJson)) {
      throw new CustomValidationError("SDL root must be a YAML object.");
    }

    const endpoints: EndpointType[] =
      yamlJson.endpoints && typeof yamlJson.endpoints === "object" && !Array.isArray(yamlJson.endpoints)
        ? Object.keys(yamlJson.endpoints).map(name => ({ id: nanoid(), name }))
        : [];

    const reclamationParse = ReclamationMinWindowSchema.safeParse(yamlJson.reclamation?.min_window);
    const reclamationMinWindow = reclamationParse.success ? reclamationParse.data : undefined;

    if (!yamlJson.services) return { placements, services, endpoints, reclamationMinWindow };

    Object.keys(yamlJson.services).forEach(svcName => {
      const svc = yamlJson.services[svcName];

      const service: Partial<ServiceType> = {
        id: nanoid(),
        title: svcName,
        image: svc.image,
        hasCredentials: !!svc.credentials,
        credentials: svc.credentials
      };

      const compute = yamlJson.profiles.compute[svcName];
      const storages = compute.resources.storage.map ? compute.resources.storage : [compute.resources.storage];

      service.profile = {
        cpu: compute.resources.cpu.units,
        arch: getCpuArch(compute.resources.cpu.attributes),
        gpu: compute.resources.gpu ? compute.resources.gpu.units : 0,
        gpuModels: compute.resources.gpu ? getGpuModels(compute.resources.gpu.attributes.vendor) : [],
        interconnect: compute.resources.gpu ? getGpuInterconnect(compute.resources.gpu.attributes) : undefined,
        hasGpu: !!compute.resources.gpu,
        ram: getResourceDigit(compute.resources.memory.size),
        ramUnit: getResourceUnit(compute.resources.memory.size),
        storage: storages.map((storage: any) => {
          const type = storage.attributes?.class || "beta3";
          const isPersistent = storage.attributes?.persistent || false;
          const isReadOnly = svc.params?.storage?.[storage.name]?.readOnly || false;

          if (type === "ram") {
            if (isPersistent) {
              throw new CustomValidationError(`A storage of class "ram" cannot be persistent.`);
            }

            if (isReadOnly) {
              throw new CustomValidationError(`A storage of class "ram" cannot be read-only.`);
            }
          }

          return {
            size: getResourceDigit(storage.size || "1Gi"),
            unit: getResourceUnit(storage.size || "1Gi"),
            isPersistent,
            type,
            name: storage.name,
            mount: svc.params?.storage?.[storage.name]?.mount || "",
            isReadOnly
          };
        })
      };
      service.profile.storage = withRootStorageFirst(service.profile.storage);

      service.command = {
        command: parseSvcCommand(svc.command),
        arg: parseSvcCommand(svc.args),
        importedCommand: tokensUnlessOnePerLine(svcTokensOf(svc.command)),
        importedArg: tokensUnlessOnePerLine(svcTokensOf(svc.args))
      };

      service.env =
        svc.env?.map((e: string) => {
          const separatorIndex = e.indexOf("=");
          const key = separatorIndex === -1 ? e : e.slice(0, separatorIndex);
          const value = separatorIndex === -1 ? undefined : e.slice(separatorIndex + 1);
          const id = (RESERVED_ENV_KEYS as readonly string[]).includes(key) ? key : nanoid();
          const isSecret = value !== undefined && secretNameOf(value) !== null;
          return isSecret ? { id, key, value, isSecret } : { id, key, value };
        }) || [];

      service.expose = [];
      svc.expose?.forEach((expose: any) => {
        const isGlobal = expose.to.find((t: any) => t.global);

        const _expose: ExposeType = {
          id: nanoid(),
          port: expose.port,
          as: expose.as || expose.port,
          proto: protoOf(expose.proto),
          global: !!isGlobal,
          to: expose.to.filter((t: any) => t.global === undefined).map((t: any) => ({ id: nanoid(), value: t.service })),
          accept: expose.accept?.map((a: string) => ({ id: nanoid(), value: a })) || [],
          ipName: isGlobal?.ip ? isGlobal.ip : "",
          hasCustomHttpOptions: !!expose.http_options,
          httpOptions: {
            maxBodySize: expose.http_options?.max_body_size ?? defaultHttpOptions.maxBodySize,
            readTimeout: expose.http_options?.read_timeout ?? defaultHttpOptions.readTimeout,
            sendTimeout: expose.http_options?.send_timeout ?? defaultHttpOptions.sendTimeout,
            nextCases: expose.http_options?.next_cases ?? defaultHttpOptions.nextCases,
            nextTries: expose.http_options?.next_tries ?? defaultHttpOptions.nextTries,
            nextTimeout: expose.http_options?.next_timeout ?? SDL_DEFAULT_NEXT_TIMEOUT,
            proxy: importHttpProxy(expose.http_options?.proxy)
          }
        };

        service.expose?.push(_expose);
      });

      const depl = yamlJson.deployment[svcName];
      const sortedPlacementNames = Object.keys(depl).sort();
      const placementName = sortedPlacementNames[0];
      const placementProfile = yamlJson.profiles.placement[placementName];

      if (!placementProfile) {
        throw new CustomValidationError(`Unable to find placement: ${placementName}`);
      }

      let placementId: string;
      if (placementPerService) {
        placementId = nanoid();
        placements.push(hydratePlacement(placementId, placementName, placementProfile));
      } else {
        const existingPlacementId = placementIdByName.get(placementName);
        if (existingPlacementId) {
          placementId = existingPlacementId;
        } else {
          placementId = nanoid();
          placementIdByName.set(placementName, placementId);
          placements.push(hydratePlacement(placementId, placementName, placementProfile));
        }
      }

      const placementPricing = placementProfile.pricing?.[svcName];
      if (!placementPricing) {
        throw new CustomValidationError(`Unable to find pricing for service "${svcName}" in placement "${placementName}"`);
      }
      const deployment = depl[placementName];

      service.placementId = placementId;
      service.pricing = {
        amount: placementPricing.amount,
        denom: placementPricing.denom
      };

      service.count = deployment.count;

      service.params = preservedParamsOf(svc.params);

      services.push(service as ServiceType);
    });

    return { placements, services, endpoints, reclamationMinWindow };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/** The form holds the container's root storage first, while an SDL may list its volumes in any order. */
function withRootStorageFirst<T extends { mount?: string }>(storages: T[]): T[] {
  return [...storages.filter(storage => !storage.mount), ...storages.filter(storage => !!storage.mount)];
}

/** The SDL spells a protocol in either case and leaves HTTP implicit. */
function protoOf(proto: unknown): ExposeType["proto"] {
  const lowercased = typeof proto === "string" ? proto.toLowerCase() : undefined;
  return lowercased === "tcp" || lowercased === "udp" ? lowercased : "http";
}

/** Service params the form carries without editing them, so a round trip writes them back unchanged. */
function preservedParamsOf(params: any): ServiceType["params"] {
  const preserved: ServiceType["params"] = {
    ...(params?.permissions ? { permissions: params.permissions } : {}),
    ...(params?.tee ? { tee: params.tee } : {})
  };
  return Object.keys(preserved).length > 0 ? preserved : undefined;
}

const getResourceDigit = (size: string): number => {
  const match = size.match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
};

const getResourceUnit = (size: string): string => {
  const match = size.match(/[a-zA-Z]+/g);
  return match ? capitalizeFirstLetter(match[0]) : "";
};

/**
 * Reads the requested CPU architecture from `cpu.attributes.arch`, leaving it off when the SDL sets none
 * so a round trip through the builder keeps writing no CPU attributes at all.
 */
const getCpuArch = (attributes: { arch?: unknown } | undefined): CpuArchType | undefined => {
  const arch = attributes?.arch;
  if (arch === undefined || arch === null) return undefined;

  const supported = CPU_ARCHITECTURES.find(candidate => candidate === arch);
  if (!supported) {
    throw new CustomValidationError(`Unsupported CPU architecture "${arch}". Expected ${CPU_ARCHITECTURES.join(" or ")}.`);
  }

  return supported;
};

/**
 * Reads the GPU interconnect opt-in from `gpu.attributes.interconnect`: an empty array (implicit `auto`
 * group) maps to `{}`, an explicit `{ group }` keeps the name, and an absent attribute leaves it off.
 */
const getGpuInterconnect = (attributes: { interconnect?: unknown }): { group?: string } | undefined => {
  const interconnect = attributes?.interconnect;
  if (interconnect === undefined || interconnect === null) return undefined;
  if (Array.isArray(interconnect)) return {};
  const group = (interconnect as { group?: unknown }).group;
  return typeof group === "string" ? { group } : {};
};

const getGpuModels = (vendor: { [key: string]: { model: string; ram: string; interface: string }[] }): ProfileGpuModelType[] => {
  const models: ProfileGpuModelType[] = [];

  for (const [vendorName, vendorModels] of Object.entries(vendor)) {
    if (vendorModels) {
      vendorModels.forEach(m => {
        models.push({
          vendor: vendorName,
          name: m.model,
          memory: m.ram || "",
          interface: m.interface || ""
        });
      });
    } else {
      models.push({
        vendor: vendorName,
        name: "",
        memory: "",
        interface: ""
      });
    }
  }

  return models;
};

/** Maps a raw SDL `http_options.proxy` block to the builder's proxy fields, retaining it when any field is explicitly defined so a defined 0 or false survives the round-trip. */
function importHttpProxy(proxy: any): ServiceExposeHTTPProxyType | undefined {
  if (!proxy || typeof proxy !== "object") return undefined;

  const mapped = {
    bufferingDisable: proxy.buffering_disable,
    bufferSize: proxy.buffer_size,
    buffersNumber: proxy.buffers_number,
    buffersSize: proxy.buffers_size,
    busyBuffersSize: proxy.busy_buffers_size,
    connectTimeout: proxy.connect_timeout
  };

  const hasAny = Object.values(mapped).some(value => value !== undefined);
  return hasAny ? mapped : undefined;
}

/**
 * Builds a fresh PlacementType from a raw SDL placement profile, lifting the
 * location-region attribute into a first-class field and dropping it from the
 * remaining attributes list.
 */
function hydratePlacement(id: string, name: string, profile: any): PlacementType {
  const rawAttributes: Record<string, string> = profile.attributes || {};
  const attributes: PlacementAttributeType[] = [];
  let region: string | undefined;

  for (const [key, value] of Object.entries(rawAttributes)) {
    if (key === "location-region") {
      region = value;
      continue;
    }
    attributes.push({ id: nanoid(), key, value });
  }

  return {
    id,
    name,
    regions: region ? [region] : [],
    attributes,
    signedBy: {
      anyOf: profile.signedBy?.anyOf ? profile.signedBy.anyOf.map((x: string) => ({ id: nanoid(), value: x })) : [],
      allOf: profile.signedBy?.allOf ? profile.signedBy.allOf.map((x: string) => ({ id: nanoid(), value: x })) : []
    }
  };
}
